<template>
  <div class="relative flex grow flex-col overflow-hidden bg-white">
    <div
      v-if="body.error"
      class="flex h-full flex-col items-center justify-center bg-red-100 px-2 py-1 text-center text-xs"
    >
      <div class="font-bold text-red-500">Error rendering preview</div>
      <div>{{ body.error }}</div>
    </div>
    <iframe
      v-else
      ref="iframeRef"
      src="about:blank"
      class="w-full grow bg-white"
    />

    <div
      v-if="props.status === 'pending'"
      class="bg-gray-2 text-gray-5 absolute bottom-3 right-3 rounded px-2 py-1 text-xs"
    >
      <PendingIndicator pending /> Rendering...
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  onMounted,
  onUnmounted,
  onWatcherCleanup,
  ref,
  watch,
} from "vue";

import PendingIndicator from "@/components/Utilities/PendingIndicator.vue";
import { Context } from "@/types/preview";
import { RenderedMarkdown, uuid } from "@/utils";

import { diff } from "./diff";
import { isSourceScripted } from "./utils";

const props = defineProps<{
  mode: Context;
  body: RenderedMarkdown;
  status: "success" | "pending";
}>();

const uid = uuid();
const iframeRef = ref<HTMLIFrameElement>();

const replacedBody = computed(() => {
  const body = props.body;
  const mode = props.mode;

  let source =
    mode === "web"
      ? body.web
      : mode === "email_free"
      ? body.email_free
      : mode === "web_free"
      ? body.web_free
      : body.email;

  if (mode === "web" || mode === "web_free") {
    // - Set links to open in a new tab by default
    // - This isn't an issue in production, but it is a little annoying that
    //   relative links (e.g. /mary-ext/archive/) doesn't work in dev, let's fix
    //   that by adding the correct origin.
    source = source.replace(
      /(?=<\/head>)/,
      import.meta.env.PROD
        ? `<base target="_blank">`
        : `<base target="_blank" href="${location.origin}">`
    );

    // It's insane that we have to do this, and I'm sure there's a better way, but for now...
    source = source.replaceAll(
      'rel="stylesheet" href="/',
      'rel="stylesheet" href="https://buttondown.com/'
    );
  }

  return source;
});

watch(iframeRef, (iframe) => {
  if (!iframe) {
    return;
  }

  const parser = new DOMParser();

  watch(
    replacedBody,
    (source, prevSource) => {
      let handled = false;

      // If this isn't our first-time, or if the source doesn't contain <script>
      // tags, let's try diffing using `micromorph` to get a tear-free experience.
      if (
        prevSource !== undefined &&
        !isSourceScripted(source) &&
        iframe.src !== "about:blank"
      ) {
        try {
          const doc = parser.parseFromString(source, "text/html");
          diff(iframe!.contentDocument!, doc);
          handled = true;
        } catch (err) {
          console.warn(`Failed to diff live preview:`, err);
        }
      }

      if (!handled) {
        // Attach a <script> tag that would notify us halfway that the document is
        // being loaded, because there doesn't seem to be a way in the iframe itself.
        //
        // We couldn't do this prior the `isSourceScripted` check
        source = source.replace(
          /(?=<\/head>)/,
          `<script>parent.postMessage("${uid}")</${"script"}>`
        );

        const blob = new Blob([source], { type: "text/html" });
        const blobUrl = URL.createObjectURL(blob);
        onWatcherCleanup(() => URL.revokeObjectURL(blobUrl));

        iframe!.contentWindow?.location.replace(blobUrl);
      }
    },
    { immediate: true }
  );
});

onMounted(() => {
  const scrollPos = ref(0);

  const onMessage = (ev: MessageEvent) => {
    if (ev.data !== uid) {
      return;
    }

    const iframe = iframeRef.value!;
    const contentWindow = iframe.contentWindow!;
    const contentDocument = iframe.contentDocument!;
    const documentElement = contentDocument.documentElement!;

    documentElement.scrollTop = scrollPos.value;

    contentWindow.addEventListener("load", () => {
      // Try restoring again, in case the first one failed.
      documentElement.scrollTop = scrollPos.value;

      contentWindow.addEventListener("scroll", () => {
        scrollPos.value = documentElement.scrollTop;
      });
    });
  };

  window.addEventListener("message", onMessage);
  onUnmounted(() => window.removeEventListener("message", onMessage));
});
</script>

<style lang="scss" scoped>
iframe {
  color-scheme: light;
}
</style>
